import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, Search, CheckCircle2, AlertCircle, ArrowRight,
  Smartphone, Tablet, Watch, ExternalLink,
} from "lucide-react";
import {
  fetchCategories, fetchBrands, fetchModelFamilies, requestQuote,
} from "@/lib/api";
import { VENDRE } from "@/constants/testIds";
import DayCountdown from "@/components/DayCountdown";

const CATEGORY_ICON = {
  smartphone: Smartphone,
  tablette: Tablet,
  "montre-connectee": Watch,
};

const STEPS = ["Catégorie", "Marque", "Modèle", "Capacité", "Diagnostic", "Devis"];

const ConditionRadio = ({ value, selected, onSelect, label, hint, testId, accent = "" }) => (
  <button
    type="button"
    onClick={() => onSelect(value)}
    data-testid={testId}
    className={`relative text-left p-4 rounded-xl border transition-all ${
      selected === value
        ? "border-[hsl(var(--gold))]/70 bg-[hsl(var(--gold))]/[0.08]"
        : "border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-elev))] hover:border-[hsl(var(--gold))]/40"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className={`font-medium ${selected === value ? "text-gold-bright" : "text-[hsl(var(--text))]"}`}>
          {label}
        </div>
        {hint && (
          <div className="text-xs text-[hsl(var(--text-muted))] mt-1 leading-snug">{hint}</div>
        )}
      </div>
      <div
        className={`size-5 rounded-full border-2 grid place-items-center shrink-0 mt-0.5 ${
          selected === value ? "border-[hsl(var(--gold))]" : "border-[hsl(var(--border-soft))]"
        }`}
      >
        {selected === value && <div className="size-2 rounded-full bg-[hsl(var(--gold))]" />}
      </div>
    </div>
    {accent && (
      <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--text-dim))]">
        {accent}
      </div>
    )}
  </button>
);

const StepIndicator = ({ current }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={VENDRE.stepIndicator}>
    {STEPS.map((label, idx) => {
      const active = idx === current;
      const done = idx < current;
      return (
        <div key={label} className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs tracking-wide ${
              active
                ? "bg-[hsl(var(--gold))]/[0.12] text-gold-bright border border-[hsl(var(--gold))]/40"
                : done
                ? "text-[hsl(var(--text-muted))]"
                : "text-[hsl(var(--text-dim))]"
            }`}
          >
            <span className="font-display text-[10px] tracking-[0.2em]">{String(idx + 1).padStart(2, "0")}</span>
            <span>{label}</span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`h-px w-4 ${done ? "bg-[hsl(var(--gold))]/40" : "bg-[hsl(var(--border-soft))]"}`} />
          )}
        </div>
      );
    })}
  </div>
);

const ProductImage = ({ src, alt, className = "size-12" }) =>
  src ? (
    <img
      src={src}
      alt={alt}
      className={`${className} object-contain bg-white/[0.04] rounded-lg p-1`}
      loading="lazy"
    />
  ) : (
    <div className={`${className} rounded-lg bg-[hsl(var(--bg-elev-2))] border border-[hsl(var(--border-soft))]/60`} />
  );

export default function Vendre() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [family, setFamily] = useState(null);
  const [variant, setVariant] = useState(null);
  const [functional, setFunctional] = useState(null);
  const [screen, setScreen] = useState(null);
  const [chassis, setChassis] = useState(null);
  const [charger, setCharger] = useState(null);

  const [quote, setQuote] = useState(null);
  const isWatch = category?.slug === "montre-connectee";

  // load categories on mount + preselect from ?categorie= query param
  useEffect(() => {
    fetchCategories().then((cats) => {
      setCategories(cats);
      const params = new URLSearchParams(location.search);
      const slug = params.get("categorie");
      if (slug) {
        const found = cats.find((c) => c.slug === slug);
        if (found) {
          setCategory(found);
          setStep(1);
        }
      }
    });
    // Run once on mount only
  }, []);

  // Load brands when entering step 1 with a category
  useEffect(() => {
    if (step !== 1 || !category) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await fetchBrands(category.slug);
        if (!cancelled) setBrands(data);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, category]);

  // Load model families when entering step 2 with a brand
  useEffect(() => {
    if (step !== 2 || !category || !brand) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await fetchModelFamilies(category.slug, brand.slug);
        if (!cancelled) setFamilies(data);
      } catch {
        if (!cancelled) setFamilies([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, category, brand]);

  const filteredFamilies = useMemo(() => {
    if (!search.trim()) return families;
    const q = search.trim().toLowerCase();
    return families.filter((f) =>
      (f.base_name || "").toLowerCase().includes(q)
    );
  }, [search, families]);

  const conditionsReady = useMemo(() => {
    const base = functional && screen && chassis;
    return isWatch ? base && charger : !!base;
  }, [functional, screen, chassis, charger, isWatch]);

  const goBack = useCallback(() => {
    if (step === 0) {
      navigate("/");
      return;
    }
    setStep((s) => Math.max(0, s - 1));
    setError(null);
    if (step === 5) setQuote(null);
  }, [step, navigate]);

  const handleSubmitQuote = async () => {
    if (!variant) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await requestQuote({
        category_slug: category.slug,
        brand_slug: brand.slug,
        model_slug: variant.slug,
        functional, screen, chassis,
        charger: isWatch ? charger : undefined,
      });
      setQuote(res);
      setStep(5);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de calculer le devis. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = () => {
    setStep(0); setCategory(null); setBrand(null); setFamily(null); setVariant(null);
    setFunctional(null); setScreen(null); setChassis(null); setCharger(null);
    setQuote(null); setError(null); setSearch("");
    navigate("/vendre");
  };

  return (
    <div className="container-mp pt-10 md:pt-14 pb-16">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <button
          onClick={goBack}
          data-testid={VENDRE.back}
          className="btn-ghost text-sm"
        >
          <ChevronLeft size={16} /> Retour
        </button>
        <StepIndicator current={step} />
      </div>

      <div className="gold-divider mb-10" />

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] flex items-start gap-3">
          <AlertCircle size={18} className="text-red-300 mt-0.5 shrink-0" />
          <div className="text-sm text-red-100">{error}</div>
        </div>
      )}

      {/* STEP 0: Category */}
      {step === 0 && (
        <section className="mp-fade-up">
          <h1 className="font-display text-3xl md:text-4xl mb-2">
            Que voulez-vous vendre&nbsp;?
          </h1>
          <p className="text-[hsl(var(--text-muted))] mb-8">
            Sélectionnez la catégorie de votre appareil pour démarrer l’estimation.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => { setCategory(c); setStep(1); }}
                data-testid={VENDRE.category(c.slug)}
                className="gold-frame text-left rounded-2xl bg-[hsl(var(--bg-elev))]/70 p-6 transition-all"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="size-20 md:size-24 rounded-xl bg-[hsl(var(--bg))] border border-[hsl(var(--border-soft))] grid place-items-center overflow-hidden">
                    {c.hero_image_url ? (
                      <img
                        src={c.hero_image_url}
                        alt={c.label}
                        className="w-full h-full object-contain p-2"
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mt-2">
                    {c.model_count}&nbsp;modèles
                  </span>
                </div>
                <div className="font-display text-xl tracking-wide mb-1">{c.label}</div>
                <div className="text-sm text-gold-bright mt-3 inline-flex items-center gap-1">
                  Démarrer <ArrowRight size={14} />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* STEP 1: Brand */}
      {step === 1 && category && (
        <section className="mp-fade-up">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mb-1">
            {category.label}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-2">
            Quelle est la marque&nbsp;?
          </h1>
          <p className="text-[hsl(var(--text-muted))] mb-8">
            Choisissez la marque de votre appareil parmi les références disponibles.
          </p>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="mp-shimmer h-20 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {brands.map((b) => (
                <button
                  key={b.slug}
                  onClick={() => { setBrand(b); setStep(2); }}
                  data-testid={VENDRE.brand(b.slug)}
                  className="gold-frame rounded-xl p-4 text-left bg-[hsl(var(--bg-elev))]/70 transition-all"
                >
                  <div className="font-medium text-[hsl(var(--text))] mb-1">{b.label}</div>
                  <div className="text-xs text-gold-bright">
                    Jusqu’à {b.max_price || "–"} €
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* STEP 2: Model family */}
      {step === 2 && brand && (
        <section className="mp-fade-up">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mb-1">
            {category.label} · {brand.label}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-2">
            Quel modèle&nbsp;?
          </h1>
          <p className="text-[hsl(var(--text-muted))] mb-6">
            Sélectionnez la référence exacte. Astuce&nbsp;: «&nbsp;Réglages › Général › Informations&nbsp;».
          </p>
          <div className="relative mb-6">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--text-dim))]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un modèle…"
              data-testid={VENDRE.search}
              className="field pl-10"
            />
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="mp-shimmer h-20 rounded-xl" />
              ))}
            </div>
          ) : filteredFamilies.length === 0 ? (
            <div className="text-sm text-[hsl(var(--text-muted))] py-12 text-center">
              Aucun modèle ne correspond à votre recherche.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {filteredFamilies.map((f) => (
                <button
                  key={f.base_name}
                  onClick={() => { setFamily(f); setVariant(f.variants[0] || null); setStep(3); }}
                  data-testid={VENDRE.family(f.base_name)}
                  className="gold-frame rounded-xl p-4 flex items-center gap-3 text-left bg-[hsl(var(--bg-elev))]/70 transition-all"
                >
                  <ProductImage src={f.image_url} alt={f.base_name} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{f.base_name}</div>
                    <div className="text-xs text-gold-bright mt-1">
                      Jusqu’à {f.max_price || "–"} €
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* STEP 3: Capacity / variant */}
      {step === 3 && family && (
        <section className="mp-fade-up">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mb-1">
            {category.label} · {brand.label} · {family.base_name}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-2">
            Quelle capacité&nbsp;?
          </h1>
          <p className="text-[hsl(var(--text-muted))] mb-6">
            Sélectionnez la variante exacte. Si votre version n’apparaît pas, choisissez la plus proche — le prix final sera confirmé sur CompaRecycle.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {family.variants.map((v) => (
              <button
                key={v.slug}
                onClick={() => { setVariant(v); setStep(4); }}
                data-testid={VENDRE.variant(v.slug)}
                className={`gold-frame rounded-xl p-4 flex items-center gap-3 text-left transition-all ${
                  variant?.slug === v.slug
                    ? "border-[hsl(var(--gold))]/70 bg-[hsl(var(--gold))]/[0.08]"
                    : "bg-[hsl(var(--bg-elev))]/70"
                }`}
              >
                <ProductImage src={v.image_url || family.image_url} alt={v.name} className="size-10" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{v.name}</div>
                  <div className="text-xs text-gold-bright mt-1">
                    Jusqu’à {v.max_price || family.max_price || "–"} €
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* STEP 4: Conditions */}
      {step === 4 && variant && (
        <section className="mp-fade-up">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mb-1">
            Diagnostic — {variant.name}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-2">
            État de votre appareil
          </h1>
          <p className="text-[hsl(var(--text-muted))] mb-8">
            Quelques questions rapides pour affiner votre estimation.
          </p>

          {/* Q1: Functional */}
          <div className="mb-8">
            <div className="font-medium mb-3">L’appareil est-il <span className="text-gold-bright">100&nbsp;% fonctionnel</span>&nbsp;?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <ConditionRadio
                value="yes" selected={functional} onSelect={setFunctional}
                label="Oui, parfaitement"
                hint="Tactile, boutons, batterie, appareil photo, son, etc. tout fonctionne."
                testId={VENDRE.functional("yes")}
              />
              <ConditionRadio
                value="no" selected={functional} onSelect={setFunctional}
                label="Non, un problème"
                hint="Un ou plusieurs éléments sont défaillants."
                testId={VENDRE.functional("no")}
              />
            </div>
          </div>

          {/* Q2: Screen */}
          <div className="mb-8">
            <div className="font-medium mb-3">État esthétique de <span className="text-gold-bright">l’écran</span></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ConditionRadio value="perfect" selected={screen} onSelect={setScreen} label="Comme neuf" hint="Aucune trace visible" testId={VENDRE.screen("perfect")} />
              <ConditionRadio value="minor" selected={screen} onSelect={setScreen} label="Micro-rayures" hint="Invisibles écran allumé" testId={VENDRE.screen("minor")} />
              <ConditionRadio value="visible" selected={screen} onSelect={setScreen} label="Rayures visibles" hint="Visibles écran allumé" testId={VENDRE.screen("visible")} />
              <ConditionRadio value="cracked" selected={screen} onSelect={setScreen} label="Cassé / fissuré" hint="Verre fissuré ou éclats" testId={VENDRE.screen("cracked")} />
            </div>
          </div>

          {/* Q3: Chassis */}
          <div className="mb-8">
            <div className="font-medium mb-3">État esthétique du <span className="text-gold-bright">contour et de l’arrière</span></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ConditionRadio value="perfect" selected={chassis} onSelect={setChassis} label="Comme neuf" hint="Aucune marque" testId={VENDRE.chassis("perfect")} />
              <ConditionRadio value="minor" selected={chassis} onSelect={setChassis} label="Légères traces" hint="Très peu visibles" testId={VENDRE.chassis("minor")} />
              <ConditionRadio value="visible" selected={chassis} onSelect={setChassis} label="Rayures visibles" hint="Marques d’usage" testId={VENDRE.chassis("visible")} />
              <ConditionRadio value="damaged" selected={chassis} onSelect={setChassis} label="Choc / déformé" hint="Coups, enfoncements" testId={VENDRE.chassis("damaged")} />
            </div>
          </div>

          {/* Q4 (watches only): Charger cable */}
          {isWatch && (
            <div className="mb-8">
              <div className="font-medium mb-3">Disposez-vous du <span className="text-gold-bright">câble chargeur d’origine</span> en état de fonctionnement&nbsp;?</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <ConditionRadio value="yes" selected={charger} onSelect={setCharger}
                  label="Oui" hint="Câble d’origine fonctionnel et en bon état."
                  testId={VENDRE.charger("yes")} />
                <ConditionRadio value="no" selected={charger} onSelect={setCharger}
                  label="Non" hint="Pas de câble, câble tiers ou non fonctionnel."
                  testId={VENDRE.charger("no")} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleSubmitQuote}
              disabled={!conditionsReady || submitting}
              data-testid={VENDRE.submit}
              className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Calcul en cours…" : "Voir mon estimation →"}
            </button>
          </div>
        </section>
      )}

      {/* STEP 5: Quote */}
      {step === 5 && quote && (
        <section className="mp-fade-up" data-testid={VENDRE.quote}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <div className="flex items-center gap-3">
              <div className="mp-ring-spin-fast shrink-0">
                <img
                  src="/one-ring.png"
                  alt=""
                  aria-hidden
                  className="w-9 h-9 md:w-10 md:h-10 select-none"
                  draggable={false}
                  style={{
                    filter:
                      "drop-shadow(0 0 8px rgba(255,170,80,0.55)) drop-shadow(0 4px 8px rgba(0,0,0,0.5))",
                  }}
                />
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-gold-bright">
                ✦ Votre estimation
              </div>
            </div>
            <DayCountdown testId="vendre-day-countdown" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-3">
            {quote.model.name}
          </h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mb-8">
            Estimation calculée à l’instant —
            <span className="text-[hsl(var(--text))]"> valable aujourd’hui uniquement.</span>{" "}
            Les prix de reprise sont réévalués chaque jour par CompaRecycle.
          </p>

          <div className="grid md:grid-cols-[1fr,1.2fr] gap-6 items-stretch">
            <div className="gold-frame rounded-2xl p-6 bg-[hsl(var(--bg-elev))]/70 flex items-center gap-5">
              <ProductImage src={quote.model.image_url} alt={quote.model.name} className="size-20" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[hsl(var(--text-muted))]">{quote.model.brand_label}</div>
                <div className="font-medium text-lg leading-tight">{quote.model.name}</div>
                {quote.model.capacity && (
                  <div className="text-xs text-gold-bright mt-2 uppercase tracking-wide">
                    {quote.model.capacity}
                  </div>
                )}
              </div>
            </div>

            <div className="gold-frame rounded-2xl p-6 bg-gradient-to-br from-[hsl(var(--gold))]/[0.10] via-transparent to-[hsl(var(--gold-deep))]/[0.18]">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-bright mb-2">
                Estimation Mon Précieux
              </div>
              <div
                className="font-display text-5xl md:text-6xl text-[hsl(var(--text))]"
                data-testid={VENDRE.quotePrice}
              >
                {quote.estimated_price.toLocaleString("fr-FR")}&nbsp;€
              </div>
              <div className="text-xs text-[hsl(var(--text-muted))] mt-3 leading-relaxed">
                Sur un prix maximum CompaRecycle de {quote.max_price?.toLocaleString("fr-FR") || "–"} €.
                Le prix exact sera confirmé sur la plateforme officielle CompaRecycle
                après vérification du diagnostic.
              </div>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-3">
            <BreakdownChip label="Fonctionnement" value={functional === "yes" ? "100% OK" : "Défaillant"} />
            <BreakdownChip label="Écran" value={SCREEN_LABEL[screen]} />
            <BreakdownChip label="Châssis" value={CHASSIS_LABEL[chassis]} />
            {isWatch && <BreakdownChip label="Câble chargeur" value={charger === "yes" ? "Oui" : "Non"} />}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={quote.comparecycle_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={VENDRE.quoteCta}
              className="btn-gold"
            >
              Finaliser sur CompaRecycle <ExternalLink size={16} />
            </a>
            <button
              type="button"
              onClick={handleRestart}
              data-testid={VENDRE.restart}
              className="btn-ghost"
            >
              Estimer un autre appareil
            </button>
          </div>

          <div className="mt-10 p-5 rounded-xl border border-[hsl(var(--border-soft))]/60 bg-[hsl(var(--bg-elev))]/40 flex items-start gap-3">
            <CheckCircle2 size={18} className="text-gold-bright mt-0.5 shrink-0" />
            <div className="text-sm text-[hsl(var(--text-muted))] leading-relaxed">
              <span className="text-[hsl(var(--text))] font-medium">Partenaire officiel.</span>{" "}
              Toutes les transactions sont assurées par CompaRecycle, en toute sécurité.
              Vous serez redirigé sur la fiche exacte de votre appareil pour finaliser
              la vente et bénéficiez de l’offre la plus avantageuse parmi plus de 50 acheteurs professionnels.
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-[hsl(var(--text-muted))] hover:text-gold-bright transition-colors">
              ← Retour à l’accueil
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

const SCREEN_LABEL = {
  perfect: "Comme neuf",
  minor: "Micro-rayures",
  visible: "Rayures visibles",
  cracked: "Cassé / fissuré",
};

const CHASSIS_LABEL = {
  perfect: "Comme neuf",
  minor: "Légères traces",
  visible: "Rayures visibles",
  damaged: "Choc / déformé",
};

const BreakdownChip = ({ label, value }) => (
  <div className="rounded-xl border border-[hsl(var(--border-soft))]/60 bg-[hsl(var(--bg-elev))]/40 p-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--text-dim))]">{label}</div>
    <div className="text-sm text-[hsl(var(--text))] mt-1">{value || "–"}</div>
  </div>
);
